import { Button } from '@/components/ui/button';
import { ChevronLeft, ExternalLink, Save, FileText, CheckCircle, Link } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import type { Tables } from '@/integrations/supabase/types';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Alert, AlertDescription } from '@/components/ui/alert';

type PropertyUnit = Tables<'property_units'>;

interface UnitEditHeaderProps {
  unit: PropertyUnit | null;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  saveStatus: 'saved' | 'saving' | 'unsaved';
  isPublished: boolean;
  onPublishChange: (published: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function UnitEditHeader({
  unit,
  isSaving,
  hasUnsavedChanges,
  saveStatus,
  isPublished,
  onPublishChange,
  onSave,
  onCancel,
}: UnitEditHeaderProps) {
  const handleCopyLink = async () => {
    if (!unit) return;
    
    const url = `${window.location.origin}/residences/${unit.unit_number}`;
    
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied!",
        description: "Unit page URL copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Please try again",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb Navigation */}
        <div className="mb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/admin/analytics">Admin</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/admin/units">Units</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  Edit Unit {unit?.unit_number || '...'}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Draft Warning Banner */}
        {!isPublished && unit && (
          <Alert className="mb-4 border-amber-500 bg-amber-50 dark:bg-amber-950">
            <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              This unit is in <strong>draft mode</strong> and will not appear on the public website or sync with HubSpot.
            </AlertDescription>
          </Alert>
        )}

        {/* Header Content */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Unit Info & Publication Toggle */}
          <div className="flex-1 min-w-0 space-y-3">
            {unit ? (
              <>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">
                    Unit {unit.unit_number}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    {unit.bedrooms} bed • {unit.bathrooms} bath • Block {unit.block} • Floor{' '}
                    {unit.floor}
                  </p>
                </div>
                
                {/* Publication Status Toggle */}
                <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                  <Switch
                    id="is-published"
                    checked={isPublished}
                    onCheckedChange={onPublishChange}
                  />
                  <div className="flex items-center gap-2">
                    {isPublished ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <Label htmlFor="is-published" className="cursor-pointer text-sm font-medium">
                          Published
                        </Label>
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        <Label htmlFor="is-published" className="cursor-pointer text-sm font-medium text-amber-700 dark:text-amber-300">
                          Draft
                        </Label>
                      </>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {isPublished ? 'Visible on website' : 'Hidden from public'}
                  </span>
                </div>
              </>
            ) : (
              <div className="h-10 w-48 bg-muted animate-pulse rounded" />
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Save Status Indicator */}
            <div className="flex items-center gap-2">
              {saveStatus === 'saved' && !hasUnsavedChanges && (
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  Saved
                </span>
              )}
              {saveStatus === 'saving' && (
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-yellow-500 animate-pulse" />
                  Saving...
                </span>
              )}
              {saveStatus === 'unsaved' && hasUnsavedChanges && (
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-orange-500" />
                  Unsaved
                </span>
              )}
            </div>

            {/* Copy Link & View on Site Buttons */}
            {unit && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  className="gap-2"
                >
                  <Link className="h-4 w-4" />
                  <span className="hidden sm:inline">Copy Link</span>
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  className="gap-2"
                >
                  <a href={`/residences/${unit.unit_number}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    <span className="hidden sm:inline">View on Site</span>
                  </a>
                </Button>
              </>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>

            <Button
              size="sm"
              onClick={onSave}
              disabled={isSaving || !unit}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
